import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  Dimensions,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Slider from '@react-native-community/slider';
import { Svg, Circle, Text as SvgText, Line, Rect, Path } from 'react-native-svg';
import Footer from '../components/Footer';
import { useTheme } from '../contexts/ThemeContext';

const { width: screenWidth } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';
const isMobile = screenWidth < 768;

const TDSCalculatorScreen = () => {
  const { colors } = useTheme();
  const [paymentAmount, setPaymentAmount] = useState('100000');
  const [tdsSection, setTdsSection] = useState('194A');
  const [hasPAN, setHasPAN] = useState(true);
  const [deducteeType, setDeducteeType] = useState('individual');
  const [financialYear, setFinancialYear] = useState('2024-25');
  const [showCertificate, setShowCertificate] = useState(false);
  const [showCompliance, setShowCompliance] = useState(false);
  const [showRateChart, setShowRateChart] = useState(false);
  const [showFAQ, setShowFAQ] = useState(false);
  const [results, setResults] = useState(null);

  // TDS Sections with rates and thresholds
  const tdsSections = [
    {
      section: '194A',
      name: 'Interest on Securities',
      description: 'Interest on bank deposits, bonds, debentures',
      panRate: 10,
      nonPanRate: 20,
      threshold: 40000,
      applicableOn: 'Interest amount',
    },
    {
      section: '194B',
      name: 'Winnings from Lottery/Crossword',
      description: 'Lottery, crossword puzzle, card game winnings',
      panRate: 30,
      nonPanRate: 30,
      threshold: 10000,
      applicableOn: 'Winning amount',
    },
    {
      section: '194BB',
      name: 'Winnings from Horse Race',
      description: 'Winnings from horse race',
      panRate: 30,
      nonPanRate: 30,
      threshold: 10000,
      applicableOn: 'Winning amount',
    },
    {
      section: '194C',
      name: 'Contractor Payments',
      description: 'Payments to contractors and sub-contractors',
      panRate: 1,
      nonPanRate: 20,
      threshold: 30000,
      applicableOn: 'Contract amount',
    },
    {
      section: '194D',
      name: 'Insurance Commission',
      description: 'Commission on insurance policies',
      panRate: 5,
      nonPanRate: 20,
      threshold: 15000,
      applicableOn: 'Commission amount',
    },
    {
      section: '194G',
      name: 'Commission on Sale of Lottery',
      description: 'Commission on sale of lottery tickets',
      panRate: 5,
      nonPanRate: 20,
      threshold: 15000,
      applicableOn: 'Commission amount',
    },
    {
      section: '194H',
      name: 'Commission/Brokerage',
      description: 'Commission, brokerage, discount payments',
      panRate: 5,
      nonPanRate: 20,
      threshold: 15000,
      applicableOn: 'Commission amount',
    },
    {
      section: '194I',
      name: 'Rent on Land/Building/Furniture',
      description: 'Rent payments for property and equipment',
      panRate: 10,
      nonPanRate: 20,
      threshold: 240000,
      applicableOn: 'Annual rent',
    },
    {
      section: '194IA',
      name: 'Sale of Immovable Property',
      description: 'Sale consideration of immovable property',
      panRate: 1,
      nonPanRate: 20,
      threshold: 5000000,
      applicableOn: 'Sale consideration',
    },
    {
      section: '194J',
      name: 'Professional/Technical Services',
      description: 'Fees for professional or technical services',
      panRate: 10,
      nonPanRate: 20,
      threshold: 30000,
      applicableOn: 'Fee amount',
    },
    {
      section: '194K',
      name: 'Income from Units',
      description: 'Income from units of mutual funds/UTI',
      panRate: 10,
      nonPanRate: 20,
      threshold: 5000,
      applicableOn: 'Income amount',
    },
    {
      section: '194LA',
      name: 'Compensation on Land Acquisition',
      description: 'Compensation on compulsory acquisition of land',
      panRate: 10,
      nonPanRate: 20,
      threshold: 250000,
      applicableOn: 'Compensation amount',
    },
    {
      section: '194M',
      name: 'Contractor Payments (Individual/HUF)',
      description: 'Contract payments to individuals/HUF',
      panRate: 5,
      nonPanRate: 20,
      threshold: 5000000,
      applicableOn: 'Annual contract value',
    },
    {
      section: '194N',
      name: 'Cash Withdrawal',
      description: 'Cash withdrawal exceeding prescribed limit',
      panRate: 2,
      nonPanRate: 2,
      threshold: 10000000,
      applicableOn: 'Withdrawal amount',
    },
    {
      section: '194O',
      name: 'E-commerce Transactions',
      description: 'Payments by e-commerce operators',
      panRate: 1,
      nonPanRate: 5,
      threshold: 500000,
      applicableOn: 'Annual gross amount',
    },
    {
      section: '194Q',
      name: 'Purchase of Goods',
      description: 'Purchase of goods exceeding prescribed limit',
      panRate: 0.1,
      nonPanRate: 5,
      threshold: 5000000,
      applicableOn: 'Annual purchase value',
    },
  ];

  const deducteeTypes = [
    { value: 'individual', label: 'Individual', description: 'Individual taxpayer' },
    { value: 'huf', label: 'HUF', description: 'Hindu Undivided Family' },
    { value: 'company', label: 'Company', description: 'Private/Public Company' },
    { value: 'firm', label: 'Partnership Firm', description: 'Partnership/LLP' },
    { value: 'cooperative', label: 'Cooperative Society', description: 'Cooperative Society' },
    { value: 'trust', label: 'Trust', description: 'Trust/AOP/BOI' },
  ];

  const financialYears = [
    { value: '2024-25', label: 'FY 2024-25', ay: 'AY 2025-26' },
    { value: '2023-24', label: 'FY 2023-24', ay: 'AY 2024-25' },
    { value: '2022-23', label: 'FY 2022-23', ay: 'AY 2023-24' },
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN').format(num);
  };

  const calculateTDS = () => {
    const amount = parseFloat(paymentAmount) || 0;
    const selectedSection = tdsSections.find(s => s.section === tdsSection);
    
    if (!selectedSection) return null;

    // Check if amount exceeds threshold
    const isAboveThreshold = amount >= selectedSection.threshold;
    
    // Get applicable rate
    const applicableRate = hasPAN ? selectedSection.panRate : selectedSection.nonPanRate;
    
    // Calculate TDS
    const tdsAmount = isAboveThreshold ? (amount * applicableRate) / 100 : 0;
    const netAmount = amount - tdsAmount;
    
    // Calculate savings with PAN
    const nonPanTDS = (amount * selectedSection.nonPanRate) / 100;
    const panTDS = (amount * selectedSection.panRate) / 100;
    const savingsWithPAN = isAboveThreshold ? nonPanTDS - panTDS : 0;

    return {
      grossAmount: amount,
      tdsRate: applicableRate,
      tdsAmount,
      netAmount,
      isAboveThreshold,
      threshold: selectedSection.threshold,
      section: selectedSection,
      savingsWithPAN,
      panRate: selectedSection.panRate,
      nonPanRate: selectedSection.nonPanRate,
    };
  };

  useEffect(() => {
    const calculatedResults = calculateTDS();
    setResults(calculatedResults);
  }, [paymentAmount, tdsSection, hasPAN, deducteeType]);

  const renderHeader = () => (
    <View style={styles.header}>
      <Text style={styles.headerTitle}>TDS Calculator</Text>
      <Text style={styles.headerSubtitle}>
        Calculate Tax Deducted at Source for various payment types with accurate rates and thresholds
      </Text>
    </View>
  );

  const renderCalculator = () => (
    <View style={styles.calculatorCard}>
      <Text style={styles.cardTitle}>TDS Calculation Details</Text>
      
      {/* Payment Amount */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Payment Amount</Text>
        <View style={styles.inputContainer}>
          <Text style={styles.currencySymbol}>₹</Text>
          <TextInput
            style={styles.textInput}
            value={paymentAmount}
            onChangeText={setPaymentAmount}
            placeholder="100000"
            keyboardType="numeric"
          />
        </View>
        <Text style={styles.helperText}>Enter the gross payment amount</Text>
      </View>

      {/* TDS Section */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>TDS Section</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.sectionScroll}>
          <View style={styles.sectionContainer}>
            {tdsSections.map((section) => (
              <TouchableOpacity
                key={section.section}
                style={[
                  styles.sectionButton,
                  tdsSection === section.section && styles.sectionButtonActive,
                ]}
                onPress={() => setTdsSection(section.section)}
              >
                <Text style={[
                  styles.sectionCode,
                  tdsSection === section.section && styles.sectionCodeActive,
                ]}>
                  {section.section}
                </Text>
                <Text style={[
                  styles.sectionName,
                  tdsSection === section.section && styles.sectionNameActive,
                ]}>
                  {section.name}
                </Text>
                <Text style={[
                  styles.sectionRate,
                  tdsSection === section.section && styles.sectionRateActive,
                ]}>
                  {section.panRate}% / {section.nonPanRate}%
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </ScrollView>
      </View>

      {/* PAN Availability */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>PAN Card Available</Text>
        <View style={styles.panContainer}>
          <TouchableOpacity
            style={[
              styles.panButton,
              hasPAN && styles.panButtonActive,
            ]}
            onPress={() => setHasPAN(true)}
          >
            <Ionicons 
              name={hasPAN ? 'checkmark-circle' : 'ellipse-outline'} 
              size={20} 
              color={hasPAN ? colors.background : colors.textSecondary} 
            />
            <Text style={[
              styles.panLabel,
              hasPAN && styles.panLabelActive,
            ]}>
              Yes, PAN Available
            </Text>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={[
              styles.panButton,
              !hasPAN && styles.panButtonActive,
            ]}
            onPress={() => setHasPAN(false)}
          >
            <Ionicons 
              name={!hasPAN ? 'checkmark-circle' : 'ellipse-outline'} 
              size={20} 
              color={!hasPAN ? colors.background : colors.textSecondary} 
            />
            <Text style={[
              styles.panLabel,
              !hasPAN && styles.panLabelActive,
            ]}>
              No PAN (Higher Rate)
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Deductee Type */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Deductee Type</Text>
        <View style={styles.deducteeContainer}>
          {deducteeTypes.map((type) => (
            <TouchableOpacity
              key={type.value}
              style={[
                styles.deducteeButton,
                deducteeType === type.value && styles.deducteeButtonActive,
              ]}
              onPress={() => setDeducteeType(type.value)}
            >
              <Text style={[
                styles.deducteeLabel,
                deducteeType === type.value && styles.deducteeLabelActive,
              ]}>
                {type.label}
              </Text>
              <Text style={[
                styles.deducteeDescription,
                deducteeType === type.value && styles.deducteeDescriptionActive,
              ]}>
                {type.description}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Financial Year */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Financial Year</Text>
        <View style={styles.fyContainer}>
          {financialYears.map((fy) => (
            <TouchableOpacity
              key={fy.value}
              style={[
                styles.fyButton,
                financialYear === fy.value && styles.fyButtonActive,
              ]}
              onPress={() => setFinancialYear(fy.value)}
            >
              <Text style={[
                styles.fyLabel,
                financialYear === fy.value && styles.fyLabelActive,
              ]}>
                {fy.label}
              </Text>
              <Text style={[
                styles.fyAY,
                financialYear === fy.value && styles.fyAYActive,
              ]}>
                {fy.ay}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>
    </View>
  );

  const renderDonutChart = () => {
    if (!results || results.tdsAmount === 0) return null;

    const { grossAmount, tdsAmount, netAmount } = results;
    const size = 200;
    const strokeWidth = 20;
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;

    const tdsPercentage = (tdsAmount / grossAmount) * 100;
    const netPercentage = (netAmount / grossAmount) * 100;

    const tdsStrokeDasharray = `${(tdsPercentage / 100) * circumference} ${circumference}`;
    const netStrokeDasharray = `${(netPercentage / 100) * circumference} ${circumference}`;
    const netStrokeDashoffset = -((tdsPercentage / 100) * circumference);

    return (
      <View style={styles.chartCard}>
        <Text style={styles.cardTitle}>Payment Breakdown</Text>
        <View style={styles.chartContainer}>
          <Svg width={size} height={size}>
            {/* Background circle */}
            <Circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={colors.border}
              strokeWidth={strokeWidth}
              fill="transparent"
            />
            
            {/* TDS amount */}
            <Circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={colors.error}
              strokeWidth={strokeWidth}
              fill="transparent"
              strokeDasharray={tdsStrokeDasharray}
              strokeLinecap="round"
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
            
            {/* Net amount */}
            <Circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={colors.success}
              strokeWidth={strokeWidth}
              fill="transparent"
              strokeDasharray={netStrokeDasharray}
              strokeDashoffset={netStrokeDashoffset}
              strokeLinecap="round"
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
            
            {/* Center text */}
            <SvgText
              x={size / 2}
              y={size / 2 - 10}
              textAnchor="middle"
              fontSize="14"
              fontWeight="600"
              fill={colors.text}
            >
              Gross Amount
            </SvgText>
            <SvgText
              x={size / 2}
              y={size / 2 + 10}
              textAnchor="middle"
              fontSize="16"
              fontWeight="bold"
              fill={colors.text}
            >
              {formatCurrency(grossAmount)}
            </SvgText>
          </Svg>
        </View>
        
        <View style={styles.chartLegend}>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: colors.error }]} />
            <Text style={styles.legendText}>
              TDS Deducted ({tdsPercentage.toFixed(1)}%)
            </Text>
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: colors.success }]} />
            <Text style={styles.legendText}>
              Net Amount ({netPercentage.toFixed(1)}%)
            </Text>
          </View>
        </View>
      </View>
    );
  };

  const renderResults = () => {
    if (!results) return null;

    const {
      grossAmount,
      tdsRate,
      tdsAmount,
      netAmount,
      isAboveThreshold,
      threshold,
      section,
      savingsWithPAN,
      panRate,
      nonPanRate,
    } = results;

    return (
      <View style={styles.resultsCard}>
        <Text style={styles.cardTitle}>TDS Calculation Results</Text>
        
        <View style={styles.sectionInfo}>
          <Text style={styles.sectionInfoTitle}>Section {section.section}</Text>
          <Text style={styles.sectionInfoDescription}>{section.description}</Text>
          <Text style={styles.sectionInfoThreshold}>
            Threshold: {formatCurrency(threshold)}
          </Text>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Gross Amount</Text>
          <Text style={styles.resultValue}>{formatCurrency(grossAmount)}</Text>
        </View>
        
        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>TDS Rate Applied</Text>
          <Text style={[styles.resultValue, { color: colors.primary }]}>
            {tdsRate}%
          </Text>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>TDS Amount</Text>
          <Text style={[styles.resultValue, { color: colors.error, fontSize: 18, fontWeight: 'bold' }]}>
            {formatCurrency(tdsAmount)}
          </Text>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Net Amount Payable</Text>
          <Text style={[styles.resultValue, { color: colors.success, fontSize: 18, fontWeight: 'bold' }]}>
            {formatCurrency(netAmount)}
          </Text>
        </View>

        {!isAboveThreshold && (
          <View style={styles.thresholdInfo}>
            <Ionicons name="information-circle" size={16} color={colors.info} />
            <Text style={styles.thresholdText}>
              No TDS applicable as amount is below threshold of {formatCurrency(threshold)}
            </Text>
          </View>
        )}

        {savingsWithPAN > 0 && (
          <View style={styles.panSavings}>
            <Text style={styles.panSavingsTitle}>PAN Benefits</Text>
            <View style={styles.panSavingsDetails}>
              <View style={styles.panSavingsItem}>
                <Text style={styles.panSavingsLabel}>With PAN ({panRate}%)</Text>
                <Text style={styles.panSavingsValue}>{formatCurrency(tdsAmount)}</Text>
              </View>
              <View style={styles.panSavingsItem}>
                <Text style={styles.panSavingsLabel}>Without PAN ({nonPanRate}%)</Text>
                <Text style={[styles.panSavingsValue, { color: colors.error }]}>
                  {formatCurrency((grossAmount * nonPanRate) / 100)}
                </Text>
              </View>
              <View style={styles.panSavingsItem}>
                <Text style={styles.panSavingsLabel}>Savings with PAN</Text>
                <Text style={[styles.panSavingsValue, { color: colors.success, fontWeight: 'bold' }]}>
                  {formatCurrency(savingsWithPAN)}
                </Text>
              </View>
            </View>
          </View>
        )}
      </View>
    );
  };

  const renderCertificate = () => (
    <View style={styles.certificateCard}>
      <TouchableOpacity
        style={styles.cardHeader}
        onPress={() => setShowCertificate(!showCertificate)}
      >
        <Text style={styles.cardTitle}>TDS Certificate (Form 16A)</Text>
        <Ionicons
          name={showCertificate ? 'chevron-up' : 'chevron-down'}
          size={24}
          color={colors.primary}
        />
      </TouchableOpacity>
      
      {showCertificate && results && results.tdsAmount > 0 && (
        <View style={styles.cardContent}>
          <View style={styles.certificatePreview}>
            <Text style={styles.certificateTitle}>CERTIFICATE UNDER SECTION 203</Text>
            <Text style={styles.certificateSubtitle}>
              [See rule 31(1)(a)]
            </Text>
            
            <View style={styles.certificateDetails}>
              <View style={styles.certificateRow}>
                <Text style={styles.certificateLabel}>1. Name and address of the Deductor:</Text>
                <Text style={styles.certificateValue}>[Deductor Details]</Text>
              </View>
              
              <View style={styles.certificateRow}>
                <Text style={styles.certificateLabel}>2. TAN of the Deductor:</Text>
                <Text style={styles.certificateValue}>[TAN Number]</Text>
              </View>
              
              <View style={styles.certificateRow}>
                <Text style={styles.certificateLabel}>3. Name and address of the Deductee:</Text>
                <Text style={styles.certificateValue}>[Deductee Details]</Text>
              </View>
              
              <View style={styles.certificateRow}>
                <Text style={styles.certificateLabel}>4. PAN of the Deductee:</Text>
                <Text style={styles.certificateValue}>[PAN Number]</Text>
              </View>
              
              <View style={styles.certificateRow}>
                <Text style={styles.certificateLabel}>5. Assessment Year:</Text>
                <Text style={styles.certificateValue}>
                  {financialYears.find(fy => fy.value === financialYear)?.ay}
                </Text>
              </View>
              
              <View style={styles.certificateRow}>
                <Text style={styles.certificateLabel}>6. Section under which tax deducted:</Text>
                <Text style={styles.certificateValue}>{results.section.section}</Text>
              </View>
              
              <View style={styles.certificateRow}>
                <Text style={styles.certificateLabel}>7. Amount paid/credited:</Text>
                <Text style={styles.certificateValue}>{formatCurrency(results.grossAmount)}</Text>
              </View>
              
              <View style={styles.certificateRow}>
                <Text style={styles.certificateLabel}>8. Tax deducted:</Text>
                <Text style={styles.certificateValue}>{formatCurrency(results.tdsAmount)}</Text>
              </View>
              
              <View style={styles.certificateRow}>
                <Text style={styles.certificateLabel}>9. Date of deduction:</Text>
                <Text style={styles.certificateValue}>[Date]</Text>
              </View>
            </View>
          </View>
        </View>
      )}
    </View>
  );

  const renderCompliance = () => {
    const complianceDates = [
      { quarter: 'Q1 (Apr-Jun)', dueDate: '31st July', returnForm: '24Q/26Q' },
      { quarter: 'Q2 (Jul-Sep)', dueDate: '31st October', returnForm: '24Q/26Q' },
      { quarter: 'Q3 (Oct-Dec)', dueDate: '31st January', returnForm: '24Q/26Q' },
      { quarter: 'Q4 (Jan-Mar)', dueDate: '31st May', returnForm: '24Q/26Q' },
    ];

    return (
      <View style={styles.complianceCard}>
        <TouchableOpacity
          style={styles.cardHeader}
          onPress={() => setShowCompliance(!showCompliance)}
        >
          <Text style={styles.cardTitle}>TDS Compliance Calendar</Text>
          <Ionicons
            name={showCompliance ? 'chevron-up' : 'chevron-down'}
            size={24}
            color={colors.primary}
          />
        </TouchableOpacity>
        
        {showCompliance && (
          <View style={styles.cardContent}>
            <Text style={styles.complianceInfo}>
              TDS returns must be filed quarterly with the following due dates:
            </Text>
            
            {complianceDates.map((item, index) => (
              <View key={index} style={styles.complianceItem}>
                <View style={styles.complianceHeader}>
                  <Text style={styles.complianceQuarter}>{item.quarter}</Text>
                  <Text style={styles.complianceDueDate}>Due: {item.dueDate}</Text>
                </View>
                <Text style={styles.complianceForm}>Form: {item.returnForm}</Text>
              </View>
            ))}
            
            <View style={styles.complianceNote}>
              <Ionicons name="warning" size={16} color={colors.warning} />
              <Text style={styles.complianceNoteText}>
                Late filing attracts penalty of ₹200 per day. TDS payment due by 7th of following month.
              </Text>
            </View>
          </View>
        )}
      </View>
    );
  };

  const renderRateChart = () => (
    <View style={styles.rateChartCard}>
      <TouchableOpacity
        style={styles.cardHeader}
        onPress={() => setShowRateChart(!showRateChart)}
      >
        <Text style={styles.cardTitle}>TDS Rate Chart</Text>
        <Ionicons
          name={showRateChart ? 'chevron-up' : 'chevron-down'}
          size={24}
          color={colors.primary}
        />
      </TouchableOpacity>
      
      {showRateChart && (
        <View style={styles.cardContent}>
          <View style={styles.rateChartHeader}>
            <Text style={styles.rateChartHeaderText}>Section</Text>
            <Text style={styles.rateChartHeaderText}>Description</Text>
            <Text style={styles.rateChartHeaderText}>PAN Rate</Text>
            <Text style={styles.rateChartHeaderText}>Non-PAN Rate</Text>
            <Text style={styles.rateChartHeaderText}>Threshold</Text>
          </View>
          
          {tdsSections.map((section, index) => (
            <View key={index} style={styles.rateChartRow}>
              <Text style={styles.rateChartCell}>{section.section}</Text>
              <Text style={[styles.rateChartCell, { flex: 2 }]}>{section.name}</Text>
              <Text style={styles.rateChartCell}>{section.panRate}%</Text>
              <Text style={styles.rateChartCell}>{section.nonPanRate}%</Text>
              <Text style={styles.rateChartCell}>{formatCurrency(section.threshold)}</Text>
            </View>
          ))}
        </View>
      )}
    </View>
  );

  const renderFAQ = () => {
    const faqData = [
      {
        question: "What is TDS and when is it applicable?",
        answer: "TDS (Tax Deducted at Source) is a method of collecting income tax where tax is deducted at the time of payment. It's applicable when payments exceed prescribed thresholds for different types of income."
      },
      {
        question: "What happens if PAN is not provided?",
        answer: "If PAN is not provided by the deductee, TDS is deducted at higher rates (typically 20% or the prescribed rate, whichever is higher) as per Section 206AA of the Income Tax Act."
      },
      {
        question: "When should TDS be deposited with the government?",
        answer: "TDS should be deposited by the 7th of the following month in which the deduction was made. For March deductions, the due date is 30th April."
      },
      {
        question: "What is Form 16A and when is it issued?",
        answer: "Form 16A is a TDS certificate issued for non-salary payments. It should be issued within 15 days of filing the quarterly TDS return or within 15 days of payment of tax, whichever is later."
      },
      {
        question: "Can TDS be claimed as a refund?",
        answer: "Yes, if TDS deducted exceeds your actual tax liability, you can claim the excess amount as a refund while filing your income tax return."
      },
      {
        question: "What are the penalties for non-compliance?",
        answer: "Penalties include: Late deposit - 1.5% per month, Non-deduction - equal to TDS amount, Late filing of returns - ₹200 per day, Non-issuance of TDS certificate - ₹100 per day."
      }
    ];

    return (
      <View style={styles.faqContainer}>
        <TouchableOpacity
          style={styles.faqHeader}
          onPress={() => setShowFAQ(!showFAQ)}
        >
          <Text style={styles.faqTitle}>Frequently Asked Questions</Text>
          <Ionicons
            name={showFAQ ? 'chevron-up' : 'chevron-down'}
            size={24}
            color={colors.primary}
          />
        </TouchableOpacity>
        
        {showFAQ && (
          <View style={styles.faqContent}>
            {faqData.map((faq, index) => (
              <View key={index} style={styles.faqItem}>
                <Text style={styles.faqQuestion}>{faq.question}</Text>
                <Text style={styles.faqAnswer}>{faq.answer}</Text>
              </View>
            ))}
          </View>
        )}
      </View>
    );
  };

  const renderInformation = () => (
    <View style={styles.infoContainer}>
      <Text style={styles.infoTitle}>About TDS (Tax Deducted at Source)</Text>
      <Text style={styles.infoSubtitle}>Understanding Tax Deduction at Source</Text>
      
      <Text style={styles.infoText}>
        TDS is a system of collecting income tax at the very source of income. 
        It ensures regular collection of taxes and reduces tax evasion. The person 
        making the payment deducts tax at prescribed rates and deposits it with the government.
      </Text>
      
      <View style={styles.tipBox}>
        <Ionicons name="document-text" size={16} color={colors.primary} />
        <Text style={styles.tipText}>
          <Text style={{ fontWeight: 'bold' }}>PAN Importance:</Text> Providing PAN 
          reduces TDS rates significantly. Without PAN, higher rates (usually 20%) apply.
        </Text>
      </View>
      
      <View style={styles.tipBox}>
        <Ionicons name="calendar" size={16} color={colors.success} />
        <Text style={styles.tipText}>
          <Text style={{ fontWeight: 'bold' }}>Compliance:</Text> TDS must be deposited 
          by 7th of following month and quarterly returns filed on time to avoid penalties.
        </Text>
      </View>
      
      <View style={styles.tipBox}>
        <Ionicons name="receipt" size={16} color={colors.info} />
        <Text style={styles.tipText}>
          <Text style={{ fontWeight: 'bold' }}>Certificate:</Text> TDS certificates 
          (Form 16A) must be issued to deductees for claiming credit in their returns.
        </Text>
      </View>
    </View>
  );

  // Styles object moved inside component to access colors
  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    header: {
      backgroundColor: colors.surface,
      paddingVertical: 40,
      paddingHorizontal: 20,
      alignItems: 'center',
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    headerTitle: {
      fontSize: 32,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
      textAlign: 'center',
    },
    headerSubtitle: {
      fontSize: 16,
      color: colors.textSecondary,
      textAlign: 'center',
      maxWidth: 600,
      lineHeight: 24,
    },
    content: {
      flex: 1,
      padding: 20,
    },
    section: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 20,
      marginBottom: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    sectionTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 16,
    },
    inputGroup: {
      marginBottom: 20,
    },
    label: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 8,
    },
    input: {
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      padding: 12,
      fontSize: 16,
      backgroundColor: colors.background,
      color: colors.text,
    },
    pickerContainer: {
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      backgroundColor: colors.background,
      overflow: 'hidden',
    },
    picker: {
      height: 50,
      color: colors.text,
    },
    switchContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: 8,
    },
    switchLabel: {
      fontSize: 16,
      color: colors.text,
      flex: 1,
    },
    calculateButton: {
      backgroundColor: colors.primary,
      paddingVertical: 16,
      paddingHorizontal: 32,
      borderRadius: 8,
      alignItems: 'center',
      marginTop: 20,
    },
    calculateButtonText: {
      color: colors.background,
      fontSize: 18,
      fontWeight: 'bold',
    },
    resultsContainer: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 20,
      marginBottom: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    resultsTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 16,
      textAlign: 'center',
    },
    resultRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    resultLabel: {
      fontSize: 16,
      color: colors.text,
      flex: 1,
    },
    resultValue: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
    },
    chartContainer: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 20,
      marginBottom: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    chartTitle: {
      fontSize: 18,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 16,
      textAlign: 'center',
    },
    chartWrapper: {
      alignItems: 'center',
      marginBottom: 20,
    },
    legendContainer: {
      flexDirection: 'row',
      justifyContent: 'center',
      flexWrap: 'wrap',
      marginTop: 16,
    },
    legendItem: {
      flexDirection: 'row',
      alignItems: 'center',
      marginHorizontal: 12,
      marginVertical: 4,
    },
    legendColor: {
      width: 12,
      height: 12,
      borderRadius: 6,
      marginRight: 6,
    },
    legendText: {
      fontSize: 14,
      color: colors.text,
    },
    certificateContainer: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 20,
      marginBottom: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    certificateHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 4,
    },
    certificateTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
    },
    certificateContent: {
      marginTop: 20,
    },
    certificateField: {
      marginBottom: 16,
    },
    certificateLabel: {
      fontSize: 14,
      color: colors.textSecondary,
      marginBottom: 4,
    },
    certificateValue: {
      fontSize: 16,
      color: colors.text,
      fontWeight: '500',
    },
    complianceContainer: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 20,
      marginBottom: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    complianceHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 4,
    },
    complianceTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
    },
    complianceContent: {
      marginTop: 20,
    },
    complianceItem: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      marginBottom: 16,
      paddingBottom: 12,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    complianceIcon: {
      marginRight: 12,
      marginTop: 2,
    },
    complianceText: {
      fontSize: 14,
      color: colors.text,
      flex: 1,
      lineHeight: 20,
    },
    rateChartContainer: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 20,
      marginBottom: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    rateChartHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 4,
    },
    rateChartTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
    },
    rateChartContent: {
      marginTop: 20,
    },
    rateChartTable: {
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      overflow: 'hidden',
    },
    rateChartRow: {
      flexDirection: 'row',
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    rateChartHeader: {
      backgroundColor: colors.primary + '20',
    },
    rateChartCell: {
      flex: 1,
      padding: 12,
      borderRightWidth: 1,
      borderRightColor: colors.border,
    },
    rateChartCellText: {
      fontSize: 14,
      color: colors.text,
      textAlign: 'center',
    },
    rateChartHeaderText: {
      fontSize: 14,
      color: colors.text,
      fontWeight: 'bold',
      textAlign: 'center',
    },
    informationContainer: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 20,
      marginBottom: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    informationTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 16,
    },
    informationItem: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      marginBottom: 16,
      paddingBottom: 12,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    informationIcon: {
      marginRight: 12,
      marginTop: 2,
    },
    informationText: {
      fontSize: 14,
      color: colors.text,
      marginLeft: 8,
      flex: 1,
      lineHeight: 18,
    },
    faqContainer: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 20,
      marginBottom: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    faqHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 4,
    },
    faqTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
    },
    faqContent: {
      marginTop: 20,
    },
    faqItem: {
      marginBottom: 20,
      paddingBottom: 16,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    faqQuestion: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 8,
    },
    faqAnswer: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
    },
    fullWidthSection: {
      paddingHorizontal: 20,
    },
    leftPanel: {
      flex: 1,
      marginRight: isMobile ? 0 : 20,
    },
    rightPanel: {
      flex: 1,
      marginTop: isMobile ? 20 : 0,
    },
    tipBox: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      backgroundColor: colors.background,
      padding: 16,
      borderRadius: 8,
      marginBottom: 12,
      borderLeftWidth: 4,
      borderLeftColor: colors.primary,
    },
    tipText: {
      fontSize: 14,
      color: colors.text,
      marginLeft: 8,
      flex: 1,
      lineHeight: 18,
    },
    // Calculator specific styles
    calculatorCard: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 20,
      marginBottom: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    cardTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 16,
      textAlign: 'center',
    },
    inputContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      backgroundColor: colors.background,
      paddingHorizontal: 12,
    },
    currencySymbol: {
      fontSize: 16,
      color: colors.textSecondary,
      marginRight: 8,
    },
    textInput: {
      flex: 1,
      paddingVertical: 12,
      fontSize: 16,
      color: colors.text,
    },
    helperText: {
      fontSize: 12,
      color: colors.textSecondary,
      marginTop: 4,
      fontStyle: 'italic',
    },
    sectionScroll: {
      marginTop: 8,
    },
    sectionContainer: {
      flexDirection: 'row',
      paddingHorizontal: 4,
    },
    sectionButton: {
      backgroundColor: colors.background,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      padding: 12,
      marginHorizontal: 4,
      minWidth: 120,
      alignItems: 'center',
    },
    sectionButtonActive: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
    },
    sectionCode: {
      fontSize: 14,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 4,
    },
    sectionCodeActive: {
      color: colors.background,
    },
    sectionName: {
      fontSize: 12,
      color: colors.textSecondary,
      textAlign: 'center',
      marginBottom: 4,
    },
    sectionNameActive: {
      color: colors.background,
    },
    sectionRate: {
      fontSize: 11,
      color: colors.textSecondary,
      textAlign: 'center',
    },
    sectionRateActive: {
      color: colors.background,
    },
    panContainer: {
      flexDirection: 'row',
      marginTop: 8,
    },
    panButton: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.background,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      padding: 12,
      marginHorizontal: 4,
    },
    panButtonActive: {
      backgroundColor: colors.primary + '20',
      borderColor: colors.primary,
    },
    panLabel: {
      fontSize: 14,
      color: colors.text,
      marginLeft: 8,
    },
    panLabelActive: {
      color: colors.primary,
      fontWeight: '600',
    },
    deducteeContainer: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      marginTop: 8,
    },
    deducteeButton: {
      backgroundColor: colors.background,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      padding: 12,
      margin: 4,
      minWidth: '30%',
      alignItems: 'center',
    },
    deducteeButtonActive: {
      backgroundColor: colors.primary + '20',
      borderColor: colors.primary,
    },
    deducteeLabel: {
      fontSize: 14,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 4,
    },
    deducteeLabelActive: {
      color: colors.primary,
    },
    deducteeDescription: {
      fontSize: 12,
      color: colors.textSecondary,
      textAlign: 'center',
    },
    deducteeDescriptionActive: {
      color: colors.primary,
    },
    fyContainer: {
      flexDirection: 'row',
      marginTop: 8,
    },
    fyButton: {
      flex: 1,
      backgroundColor: colors.background,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      padding: 12,
      marginHorizontal: 4,
      alignItems: 'center',
    },
    fyButtonActive: {
      backgroundColor: colors.primary + '20',
      borderColor: colors.primary,
    },
    fyLabel: {
      fontSize: 14,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 4,
    },
    fyLabelActive: {
      color: colors.primary,
    },
    fyAY: {
      fontSize: 12,
      color: colors.textSecondary,
    },
    fyAYActive: {
      color: colors.primary,
    },
    resultItem: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    thresholdInfo: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.info + '20',
      padding: 12,
      borderRadius: 8,
      marginTop: 16,
    },
    thresholdText: {
      fontSize: 14,
      color: colors.info,
      marginLeft: 8,
      flex: 1,
    },
    panSavings: {
      backgroundColor: colors.success + '10',
      borderRadius: 8,
      padding: 16,
      marginTop: 16,
    },
    panSavingsTitle: {
      fontSize: 16,
      fontWeight: 'bold',
      color: colors.success,
      marginBottom: 12,
    },
    panSavingsDetails: {
      gap: 8,
    },
    panSavingsItem: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 4,
    },
    panSavingsLabel: {
      fontSize: 14,
      color: colors.text,
    },
    panSavingsValue: {
      fontSize: 14,
      fontWeight: '600',
      color: colors.text,
    },
    certificateRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      paddingVertical: 8,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
  });

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {renderHeader()}
      
      <View style={styles.content}>
        <View style={styles.leftPanel}>
          {renderCalculator()}
        </View>
        
        <View style={styles.rightPanel}>
          {renderDonutChart()}
          {renderResults()}
        </View>
      </View>
      
      {renderCertificate()}
      {renderCompliance()}
      {renderRateChart()}
      {renderInformation()}
      
      <View style={styles.fullWidthSection}>
        {renderFAQ()}
      </View>
      
      <Footer />
    </ScrollView>
  );
};

export default TDSCalculatorScreen;